import { useEffect, useMemo, useState } from "react";
import type {
  AnalysisResponse,
  PolizzaExtractResponse,
} from "@/lib/api/extract";
import type { DocumentKind, UnifiedRunStatus } from "./status-domain";

const RESULT_TRANSITION_MIN_MS = 360;

export function useRunSelectionTransition({
  analysis,
  errorMessage,
  polizzaAnalysis,
  runStatus,
}: {
  analysis: AnalysisResponse | null;
  errorMessage: string | null;
  polizzaAnalysis: PolizzaExtractResponse | null;
  runStatus: UnifiedRunStatus | null;
}) {
  const [requestedRunId, setRequestedRunId] = useState<string | null>(null);
  const [resultTransitionReady, setResultTransitionReady] = useState(true);

  const selectionResolved = useMemo(() => {
    if (requestedRunId === null) return true;
    if (!runStatus) return false;
    if (`${runStatus.kind}:${runStatus.id}` !== requestedRunId) return false;
    if (runStatus.status !== "completed") return true;
    return runStatus.kind === "fatture_ddt"
      ? Boolean(analysis)
      : Boolean(polizzaAnalysis);
  }, [analysis, polizzaAnalysis, requestedRunId, runStatus]);

  useEffect(() => {
    if (requestedRunId === null) {
      setResultTransitionReady(true);
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setResultTransitionReady(true);
    }, RESULT_TRANSITION_MIN_MS);

    return () => window.clearTimeout(timeoutId);
  }, [requestedRunId]);

  useEffect(() => {
    if (requestedRunId === null) return;
    if (errorMessage) {
      setRequestedRunId(null);
      return;
    }
    if (selectionResolved && resultTransitionReady) {
      setRequestedRunId(null);
    }
  }, [errorMessage, requestedRunId, resultTransitionReady, selectionResolved]);

  const beginRunSelection = (runId: number, kind: DocumentKind) => {
    if (runId <= 0) return;

    const selectedId = `${kind}:${runId}`;
    const currentId = runStatus ? `${runStatus.kind}:${runStatus.id}` : null;
    const hasResolvedCurrentResult =
      runStatus?.status !== "completed" ||
      (runStatus?.kind === "fatture_ddt" && analysis) ||
      (runStatus?.kind === "polizza_carico" && polizzaAnalysis);

    if (currentId === selectedId && hasResolvedCurrentResult) return;
    setRequestedRunId(selectedId);
    setResultTransitionReady(false);
  };

  return {
    beginRunSelection,
    pendingRunId: requestedRunId,
    isPendingRun: (runId: number, kind: DocumentKind) =>
      requestedRunId === `${kind}:${runId}`,
    isResultTransitioning:
      requestedRunId !== null && (!selectionResolved || !resultTransitionReady),
  };
}
